import React, { useState } from 'react';

const ColorPicker = ({ color, opacity, onChange }) => {
  const [localColor, setLocalColor] = useState(color);
  const [localOpacity, setLocalOpacity] = useState(opacity);
  const [hexInputValue, setHexInputValue] = useState(color.toUpperCase());

  // Update local state when props change
  React.useEffect(() => {
    setLocalColor(color);
    setHexInputValue(color.toUpperCase());
  }, [color]);

  React.useEffect(() => {
    setLocalOpacity(opacity);
  }, [opacity]);

  const handleHexInputChange = (hexValue) => {
    console.log('Hex input changed:', hexValue);
    setHexInputValue(hexValue); // Always update the display value
    
    // Clean the input - remove # if present, convert to uppercase
    let cleanHex = hexValue.replace('#', '').toUpperCase();
    
    // Only allow valid hex characters
    cleanHex = cleanHex.replace(/[^0-9A-F]/g, '');
    
    // If we have a valid 6-character hex, update the color
    if (cleanHex.length === 6) {
      const newColor = `#${cleanHex}`;
      setLocalColor(newColor);
      onChange({ color: newColor, opacity: localOpacity });
      console.log('Valid hex color applied:', newColor);
    }
  };

  const handleHexInputBlur = (e) => {
    console.log('Hex input blur:', e.target.value);
    const value = e.target.value.replace('#', '');
    if (value.length === 6 && /^[0-9A-F]{6}$/i.test(value)) {
      const newColor = `#${value.toUpperCase()}`;
      setLocalColor(newColor);
      setHexInputValue(newColor.toUpperCase());
      onChange({ color: newColor, opacity: localOpacity });
    } else {
      // Revert to current valid color if invalid input
      console.log('Invalid hex, reverting to:', localColor);
      setHexInputValue(localColor.toUpperCase());
    }
  };

  const handleColorChange = (newColor) => {
    setLocalColor(newColor);
    onChange({ color: newColor, opacity: localOpacity });
  };

  const handleOpacityChange = (newOpacity) => {
    setLocalOpacity(newOpacity);
    onChange({ color: localColor, opacity: newOpacity });
  };

  const swatchColors = [
    { name: '[None]', color: 'transparent', pattern: 'checkerboard' },
    { name: '[Registration]', color: '#000000', border: true },
    { name: '[Paper]', color: '#FFFFFF' },
    { name: '[Black]', color: '#000000' },
    { name: '#00AEEF', color: '#00AEEF' },
    { name: '#ED1C24', color: '#ED1C24' },
    { name: '#FFF200', color: '#FFF200' },
    { name: '#D62D20', color: '#D62D20' },
    { name: '#39B54A', color: '#39B54A' }
  ];

  return (
    <div className="space-y-2">
      <div>
        <label className="block text-gray-300 mb-1" style={{ fontSize: '9px' }}>Color</label>
        <div className="flex items-center gap-1.5">
          <div 
            className="border border-gray-600 cursor-pointer flex-shrink-0"
            style={{ 
              width: '25px', 
              height: '19px',
              backgroundColor: localColor 
            }}
            onClick={() => document.getElementById('colorInput').click()}
          />
          <input
            id="colorInput"
            type="color"
            value={localColor}
            onChange={(e) => handleColorChange(e.target.value)}
            className="w-0 h-0 opacity-0"
          />
          <input
            type="text"
            value={hexInputValue}
            onChange={(e) => {
              console.log('Input onChange event:', e.target.value);
              handleHexInputChange(e.target.value);
            }}
            onBlur={handleHexInputBlur}
            onClick={(e) => {
              console.log('Input clicked');
              e.target.select();
            }}
            onFocus={(e) => {
              console.log('Input focused');
              e.target.select();
            }}
            onKeyDown={(e) => {
              console.log('Key pressed in input:', e.key);
              e.stopPropagation(); // Prevent global shortcuts
            }}
            className="flex-1 bg-gray-700 text-gray-300 px-1.5 py-0.5 border border-gray-600 focus:border-blue-500 focus:outline-none"
            style={{ 
              backgroundColor: '#4a4a4a', 
              fontSize: '14px',
              height: '19px',
              userSelect: 'text',
              cursor: 'text',
              pointerEvents: 'auto'
            }}
            placeholder="#FFFFFF"
            maxLength="7"
            spellCheck={false}
            autoComplete="off"
          />
        </div>
      </div>

      <div>
        <label className="block text-gray-300 mb-0.5" style={{ fontSize: '9px' }}>
          Opacity: {Math.round(localOpacity * 100)}%
        </label>
        <div className="relative">
          <div 
            className="w-full h-1.5 cursor-pointer"
            style={{
              background: `linear-gradient(to right, #666 0%, #666 ${localOpacity * 100}%, #000 ${localOpacity * 100}%, #000 100%)`
            }}
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const x = e.clientX - rect.left;
              const percentage = x / rect.width;
              handleOpacityChange(Math.max(0, Math.min(1, percentage)));
            }}
          />
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={localOpacity}
            onChange={(e) => handleOpacityChange(parseFloat(e.target.value))}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
          <div 
            className="absolute top-1/2 rounded-full transform -translate-y-1/2 pointer-events-none border"
            style={{ 
              left: `calc(${localOpacity * 100}% - 5px)`,
              width: '10px',
              height: '10px',
              backgroundColor: '#000',
              borderColor: '#fff',
              borderWidth: '1px'
            }}
          />
        </div>
      </div>

      <div>
        <label className="block text-gray-300 mb-1" style={{ fontSize: '9px' }}>Swatches</label>
        <div className="space-y-0">
          {swatchColors.map((swatch, index) => (
            <button
              key={index}
              className="w-full flex items-center gap-1.5 py-0.5 px-0.5 hover:bg-gray-700/30 transition-colors text-left"
              onClick={() => swatch.color !== 'transparent' && handleColorChange(swatch.color)}
            >
              <div className="relative flex-shrink-0" style={{ width: '13px', height: '13px' }}>
                {swatch.pattern === 'checkerboard' ? (
                  <div className="w-full h-full relative border border-gray-500">
                    <div className="absolute inset-0 grid grid-cols-2 grid-rows-2">
                      <div className="bg-red-500"></div>
                      <div className="bg-white"></div>
                      <div className="bg-white"></div>
                      <div className="bg-red-500"></div>
                    </div>
                  </div>
                ) : (
                  <div 
                    className={`w-full h-full ${swatch.border ? 'border border-gray-400' : 'border border-gray-600'}`}
                    style={{ backgroundColor: swatch.color }}
                  />
                )}
              </div>
              <span className="text-gray-300" style={{ fontSize: '8px' }}>{swatch.name}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ColorPicker;
